/**
 * Page guides — the data model behind the in-app help framework.
 *
 * A `PageGuide` augments the route-mapped article {@link ./registry.ts} with
 * the three things an article can't express: an ordered first-run **walkthrough**,
 * **per-panel** help anchored to DOM elements, and state-driven **guided actions**
 * ("suggested next steps"). It is the single source consumed by the onboarding
 * tour, the inline panel "?" buttons, and the Help panel's "This page" tab.
 */

import { boardMeetingsGuide } from "./guides/board-meetings.js";
import { dataRoomsGuide } from "./guides/data-rooms.js";
import { documentsGuide } from "./guides/documents.js";
import { engagementPlanGuide } from "./guides/engagement-plan.js";
import { ideationMapsGuide } from "./guides/ideation-maps.js";
import { ideationGuide } from "./guides/ideation.js";
import { myVaultGuide } from "./guides/my-vault.js";
import { notesGuide } from "./guides/notes.js";
import { partnerVaultsGuide } from "./guides/partner-vaults.js";
import { portalUsersGuide } from "./guides/portal-users.js";
import { programmeCollaborationGuide } from "./guides/programme-collaboration.js";
import { projectsGuide } from "./guides/projects.js";

/** A single step in the first-run walkthrough. */
interface GuideStep {
  /** Stable id, e.g. "capture". */
  id: string;
  title: string;
  /** 1–2 sentences (markdown-lite, rendered by HelpMarkdown). */
  body: string;
  /** `data-help-anchor` value to spotlight; omit for a centred modal step. */
  anchorId?: string;
}

/** Contextual help for one panel, surfaced via its inline "?" and the tour. */
export interface PanelHelp {
  /** Matches the `data-help-anchor` on the panel root. */
  anchorId: string;
  title: string;
  body: string;
  /** Optional link into {@link ./registry.ts} `helpArticles`. */
  relatedArticleId?: string;
}

/**
 * A generic, page-agnostic snapshot the guided-action predicates read. Each
 * page builds this from its own hooks and passes it to {@link NextActions}, so
 * the framework never couples to one page's schema.
 */
export interface PageStateContext {
  counts: Record<string, number>;
  flags: Record<string, boolean>;
  role: string | null;
}

/** A suggested next step, shown only when its predicate holds. */
export interface GuidedAction {
  id: string;
  label: string;
  /** Why this is suggested now. */
  description: string;
  /** Pure predicate over the page snapshot — no hooks, no DOM. */
  when: (ctx: PageStateContext) => boolean;
  /** Scroll/focus target (`data-help-anchor`) when the action is taken. */
  actionAnchorId?: string;
  /** Only surface to these roles (omit = any role). */
  requiresRole?: string[];
}

export interface PageGuide {
  /** Matches `HelpArticle.route`. */
  route: string;
  /** Bump to re-trigger the first-run tour for everyone. */
  version: number;
  /** Links to the overview article in {@link ./registry.ts}. */
  overviewArticleId: string;
  steps: GuideStep[];
  panels: PanelHelp[];
  guidedActions: GuidedAction[];
}

export const pageGuides: PageGuide[] = [
  ideationMapsGuide,
  ideationGuide,
  notesGuide,
  projectsGuide,
  dataRoomsGuide,
  boardMeetingsGuide,
  documentsGuide,
  myVaultGuide,
  programmeCollaborationGuide,
  partnerVaultsGuide,
  portalUsersGuide,
  engagementPlanGuide,
];

/** Resolve the guide for a route (exact match, then longest path-prefix). */
export function getPageGuide(route: string): PageGuide | null {
  const exact = pageGuides.find((guide) => guide.route === route);
  if (exact) return exact;
  const prefix = pageGuides
    .filter((guide) => route.startsWith(`${guide.route}/`))
    .sort((a, b) => b.route.length - a.route.length)[0];
  return prefix ?? null;
}

/** Filter a guide's actions to those whose predicate holds and role allows. */
export function visibleGuidedActions(guide: PageGuide, ctx: PageStateContext): GuidedAction[] {
  return guide.guidedActions.filter((action) => {
    if (action.requiresRole && (!ctx.role || !action.requiresRole.includes(ctx.role))) {
      return false;
    }
    return action.when(ctx);
  });
}
